import React, { Fragment, useMemo } from 'react';
import { Box } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { Icon } from '@ui/Icon';
import { EllipsisText } from '@ui/EllipsisText';
import { ElementKind, type Baseline } from '@shared/models/project.model';
import {
  getElementById,
  getPathToElement,
} from '@shared/helpers/recursive-element.helpers';
import { getElementName } from '@shared/helpers/element.helpers';
import { getElementCategory } from '@shared/helpers/element-category.helpers';
import { getElementIconCategoryName } from '@features/elements/helpers/get-element-category-icon-name.helper';
import { getElementGroupingHelpers } from '@features/elements/element-grouping.model';
import {
  normalizeSbefScopeGroupKey,
  SITE_OPERATIONS_SBEF_GROUP_KEY,
} from '@shared/helpers/sbef-grouping.helpers';
import { escapeRegExp } from 'lodash';

export type BreadcrumbSegment = {
  id: string;
  label: string;
  /** Fixed prefix that never truncates, e.g. "4." for SBEF group segments. */
  prefix?: string;
  icon?: ReturnType<typeof getElementIconCategoryName>;
  /**
   * When set, overrides the icon-based auto-detection for whether the segment
   * acts as a clickable button. Useful for callers (e.g. FocusViewHeader) that
   * want all segments to be navigable regardless of icon presence.
   */
  clickable?: boolean;
};

type BreadcrumbRowProps = {
  segments: BreadcrumbSegment[];
  onSegmentClick?: (id: string) => void;
  nowrap?: boolean;
};

type ElementBreadcrumbProps = {
  /** Version that owns the element — used to resolve the path and product names. */
  version: Baseline;
  /** Target element id; the breadcrumb shows the path from the version root to it. */
  elementId: string;
  /** Optional click handler invoked with the clicked segment's element id. */
  onSegmentClick?: (elementId: string) => void;
  /**
   * When true, prepends the SBEF group segment (e.g. "A. STOMMEN") to the
   * breadcrumb, matching the visual language of the focus view header.
   */
  showGroup?: boolean;
  /**
   * When true, forces the breadcrumb onto a single line — segments shrink and
   * their labels truncate with ellipsis exactly as in the focus view header.
   */
  nowrap?: boolean;
};

/**
 * Splits a formatted group label (e.g. "4. OUTER ROOF") into a fixed prefix
 * ("4.") and the truncatable rest ("OUTER ROOF"). Returns `null` when the
 * label has no recognised group-key prefix so the full label is kept intact.
 */
export const extractGroupPrefix = (
  label: string,
): { prefix: string; restLabel: string } | null => {
  const dotIdx = label.indexOf('. ');
  if (dotIdx === -1) return null;
  const potentialKey = label.slice(0, dotIdx);
  if (!/^[A-Z0-9]+$/.test(potentialKey)) return null;
  return { prefix: `${potentialKey}.`, restLabel: label.slice(dotIdx + 2) };
};

/**
 * Returns the flex-shrink value for a segment at `index` in a nowrap row of
 * `total` segments. Uses an exponential scale so early segments shrink away
 * before later ones are affected — the leaf (last) always shrinks last.
 */
const getSegmentFlexShrink = (index: number, total: number): number =>
  Math.pow(2000, total - 1 - index);

export const formatGroupLabel = (
  groupKey: string,
  fallbackLabel: string,
  customLabel?: string,
): string => {
  const toUpper = (s: string) => s.toUpperCase();
  const normalizedKey = normalizeSbefScopeGroupKey(groupKey);
  if (!customLabel) return toUpper(fallbackLabel);
  if (normalizedKey === SITE_OPERATIONS_SBEF_GROUP_KEY) {
    const keyPrefixRegExp = /^9(?:\.|\s)/i;
    const hasKeyPrefix = keyPrefixRegExp.test(customLabel.trim());
    return toUpper(hasKeyPrefix ? customLabel : `9. ${customLabel}`);
  }
  const keyPrefixRegExp = new RegExp(
    `^${escapeRegExp(normalizedKey)}(?:\\.|\\s)`,
    'i',
  );
  const hasKeyPrefix = keyPrefixRegExp.test(customLabel.trim());
  return toUpper(
    hasKeyPrefix ? customLabel : `${normalizedKey}. ${customLabel}`,
  );
};

/**
 * Presentational breadcrumb row.
 *
 * Renders a pre-computed list of {@link BreadcrumbSegment}s as a horizontal
 * flex row of clickable/static pills separated by `/`. Segments shrink in
 * reverse priority order when `nowrap` is true: the leaf (last segment) is
 * preserved as long as possible.
 *
 * Use {@link ElementBreadcrumb} for the common case where segments are derived
 * from a version + element id. Use this component directly when segments are
 * built externally (e.g. {@link FocusViewHeader}).
 */
export const BreadcrumbRow: React.FC<BreadcrumbRowProps> = ({
  segments,
  onSegmentClick,
  nowrap = false,
}) => {
  const { classes, cx } = useStyles();

  return (
    <Box className={cx(classes.root, nowrap && classes.rootNowrap)}>
      {segments.map((segment, index) => {
        const isLast = index === segments.length - 1;
        const clickable =
          typeof segment.clickable === 'boolean'
            ? segment.clickable && !!onSegmentClick
            : !!onSegmentClick && !!segment.icon;
        return (
          <Fragment key={segment.id}>
            {segment.prefix && (
              <Box component="span" className={classes.prefixItem}>
                {segment.prefix}
              </Box>
            )}
            <Box
              component={clickable ? 'button' : 'span'}
              type={clickable ? 'button' : undefined}
              className={cx(
                classes.segment,
                segment.icon && classes.segmentWithIcon,
                clickable && classes.segmentClickable,
                isLast && classes.segmentLeaf,
              )}
              sx={
                nowrap
                  ? { flexShrink: getSegmentFlexShrink(index, segments.length) }
                  : undefined
              }
              onClick={
                clickable ? () => onSegmentClick?.(segment.id) : undefined
              }
            >
              {segment.icon && (
                <Box className={classes.icon}>
                  <Icon
                    category="elementCategory"
                    icon={segment.icon}
                    size={12}
                  />
                </Box>
              )}
              <EllipsisText className={classes.label}>
                {segment.label}
              </EllipsisText>
            </Box>
          </Fragment>
        );
      })}
    </Box>
  );
};

/**
 * Breadcrumb-style trail of an element's ancestors inside a building version.
 *
 * Resolves the path from `version` root to `elementId` and delegates rendering
 * to {@link BreadcrumbRow}. Returns `null` when the element cannot be located.
 *
 * When {@link ElementBreadcrumbProps.showGroup} is true, the SBEF group is
 * prepended as the first segment, matching the focus view header visual language.
 */
export const ElementBreadcrumb: React.FC<ElementBreadcrumbProps> = ({
  version,
  elementId,
  onSegmentClick,
  showGroup = false,
  nowrap = false,
}) => {
  const segments = useMemo<BreadcrumbSegment[]>(() => {
    const target = getElementById(version, elementId, false);
    if (!target) return [];

    // getPathToElement returns ancestors only — append the target so the leaf is shown
    const ancestors = getPathToElement(version, elementId, false) ?? [];
    const elementPath = [
      ...ancestors.filter((el) => el?.kind === ElementKind.Element),
      target,
    ];

    const elementSegments = elementPath.map<BreadcrumbSegment>((el) => {
      const category = getElementCategory(el);
      return {
        id: el.id,
        label: getElementName(el, version.products),
        icon: category ? getElementIconCategoryName(category.id) : undefined,
      };
    });

    if (!showGroup) return elementSegments;

    const groupHelpers = getElementGroupingHelpers('sbef')?.helpers;
    const firstElement = elementPath[0];
    if (!groupHelpers || !firstElement) return elementSegments;

    const groupKey = groupHelpers.getGroupKey(firstElement);
    const rawGroupLabel = formatGroupLabel(
      groupKey,
      groupHelpers.getGroupLabel(groupKey),
      version.sbefGroupNames?.[groupKey],
    );
    const extracted = extractGroupPrefix(rawGroupLabel);
    const groupSegment: BreadcrumbSegment = {
      id: groupKey,
      label: extracted?.restLabel ?? rawGroupLabel,
      prefix: extracted?.prefix,
    };

    return [groupSegment, ...elementSegments];
  }, [version, elementId, showGroup]);

  if (!segments.length) return null;

  return (
    <BreadcrumbRow
      segments={segments}
      onSegmentClick={onSegmentClick}
      nowrap={nowrap}
    />
  );
};

const useStyles = makeStyles()(({ palette, spacing }) => ({
  root: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: '2px',
    minWidth: 0,
    width: '100%',
  },
  rootNowrap: {
    flexWrap: 'nowrap',
    overflow: 'hidden',
  },
  segment: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    minWidth: 0,
    padding: spacing(0.25, 0.5),
    borderRadius: 4,
    border: 'none',
    background: 'none',
    appearance: 'none',
    WebkitAppearance: 'none',
    color: 'rgba(0,0,0,0.7)',
    cursor: 'default',
  },
  segmentWithIcon: {
    // icon(12px) + gap(4px) + padding(4px * 2) = 24px minimum
    // prevents icon overflow when flex-shrink collapses text to zero
    minWidth: 24,
  },
  segmentClickable: {
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: palette.action.hover,
      color: 'rgba(0,0,0,0.9)',
    },
  },
  segmentLeaf: {
    fontWeight: 500,
    color: 'rgba(0,0,0,0.9)',
  },
  icon: {
    display: 'flex',
    flexShrink: 0,
    opacity: 0.4,
  },
  prefixItem: {
    flexShrink: 0,
    whiteSpace: 'nowrap' as const,
    fontSize: 11,
    lineHeight: 1.2,
    color: 'rgba(0,0,0,0.7)',
    padding: spacing(0.25, 0),
  },
  label: {
    fontSize: 11,
    lineHeight: 1.2,
  },
}));
